import { cssVars } from '@lmc/design-system/tokens'
import { Form as AntdForm, Input, Radio, type RadioGroupProps } from 'antd'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import {
  type StepConfirmDiscard,
  useConfirmDiscard
} from './StepConfirmDiscard'
import StepField, { useStepFieldLabelId } from './StepField'
import { isBlank } from './stepValues'

type StepYesNoProps = Omit<RadioGroupProps, 'options'> & {
  confirmNo?: StepConfirmDiscard
  confirmYes?: StepConfirmDiscard
  'data-testid'?: string
}

/**
 * A yes/no answer, valued `true`/`false`; put it inside a `StepField`. With
 * `confirmNo` or `confirmYes`, that answer asks first when it would drop
 * something, so neither direction loses what was recorded without a warning.
 */
const StepYesNo = ({
  confirmNo,
  confirmYes,
  onChange,
  ...props
}: StepYesNoProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const no = useConfirmDiscard(confirmNo, props['data-testid'])
  const yes = useConfirmDiscard(confirmYes, `${props['data-testid']}-yes`)
  const labelId = useStepFieldLabelId()

  return (
    <>
      {/* antd's group has no radiogroup role, so this names the pair. */}
      <div role="radiogroup" aria-labelledby={labelId}>
        <Row
          {...props}
          onChange={(event) =>
            (event.target.value ? yes : no).ask(() => onChange?.(event))
          }
          options={[
            { label: t(incidentReportsConstants.CASE_V2_YES), value: true },
            { label: t(incidentReportsConstants.CASE_V2_NO), value: false }
          ]}
        />
      </div>
      {no.modal}
      {yes.modal}
    </>
  )
}

type StepYesNoFieldProps = {
  /** Shown under Yes. */
  children?: ReactNode
  confirmNo?: StepConfirmDiscard
  detailsLabel: ReactNode
  detailsName: string
  detailsTestId: string
  label: ReactNode
  name: string
  testId: string
}

/**
 * A required yes/no question: `children` under Yes, a details box under No.
 * Answering Yes over details given for No asks before removing them.
 */
export const StepYesNoField = ({
  children,
  confirmNo,
  detailsLabel,
  detailsName,
  detailsTestId,
  label,
  name,
  testId
}: StepYesNoFieldProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)

  return (
    <>
      <StepField name={name} isRequired label={label}>
        <StepYesNo
          confirmNo={confirmNo}
          confirmYes={{
            body: t(incidentReportsConstants.CASE_V2_CONFIRM_YES_BODY),
            isNeeded: (form) => !isBlank(form.getFieldValue(detailsName)),
            onConfirm: (form) => form.setFieldValue(detailsName, undefined),
            title: t(incidentReportsConstants.CASE_V2_CONFIRM_YES_TITLE)
          }}
          data-testid={testId}
        />
      </StepField>
      <AntdForm.Item noStyle dependencies={[name]}>
        {({ getFieldValue }) =>
          getFieldValue(name) === true
            ? children
            : getFieldValue(name) === false && (
                <StepField name={detailsName} label={detailsLabel}>
                  <Input.TextArea
                    data-testid={detailsTestId}
                    autoSize={{ minRows: 3 }}
                  />
                </StepField>
              )
        }
      </AntdForm.Item>
    </>
  )
}

const { color, spacing } = cssVars

/**
 * Yes and No side by side, 24px apart, each a 16px radio 8px from its label,
 * as designed. The legacy ui-kit radio stylesheet stacks every antd radio
 * group in a column and boxes each option (its background is `!important`).
 */
const Row = styled(Radio.Group)`
  && {
    display: flex;
    flex-direction: row;
    gap: ${spacing[300]};
    width: auto;
  }

  && .ant-radio-wrapper {
    align-items: center;
    background: none !important;
    border: 0;
    color: ${color.text.primary};
    margin: 0;
    padding: 0;
  }
`

export default StepYesNo
